import { useState } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../../api/dates";
import { useExceptions, useResolveFlag, type ExceptionsView } from "../../api/sites";
import { Pill } from "../../components/ui";
import "../../styles/sites.css";
import SectionTabs from "./SectionTabs";
import { flagLabel } from "./status";

/** Every unresolved flag across the register, newest first: the office's
 * queue of document issues to work through, resolvable in place. The code
 * pills narrow it to one kind of issue, which is how it gets worked -- all the
 * missing positions, then all the DN mismatches. */
export default function ExceptionsPage() {
  const [code, setCode] = useState("");
  const [showResolved, setShowResolved] = useState(false);
  const view = useExceptions(code, showResolved);
  const data = view.data;
  const blocking = new Set(data?.blocking_codes ?? []);
  const openTotal = (data?.codes ?? []).reduce((sum, c) => sum + c.count, 0);

  return (
    <div className="page-bleed site-section">
      <SectionTabs />
      <div className="site-page">
        <div className="site-code-filters" role="group" aria-label="Filter by flag">
          <Pill on={code === ""} count={data ? openTotal : undefined} onToggle={() => setCode("")}>
            All
          </Pill>
          {(data?.codes ?? []).map((c) => (
            <Pill key={c.code} on={code === c.code} count={c.count} onToggle={() => setCode(code === c.code ? "" : c.code)}>
              {flagLabel(c.code)}
            </Pill>
          ))}
          <label className="check">
            <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} /> Show resolved
          </label>
        </div>

        <section className="card" aria-label="Exceptions" aria-busy={view.isFetching}>
          {view.isError ? (
            <p className="notice bad site-register-note" role="alert">
              Could not load the exceptions: {view.error.message}
            </p>
          ) : null}
          {view.isPending ? (
            <p className="empty-state">Loading exceptions…</p>
          ) : data && data.rows.length === 0 ? (
            <p className="empty-state">{showResolved || code !== "" ? "Nothing here." : "Nothing outstanding."}</p>
          ) : data ? (
            <ul className="exception-list">
              {data.rows.map((row) => (
                <ExceptionRow key={row.id} row={row} blocking={blocking.has(row.code) || row.blocking} />
              ))}
            </ul>
          ) : null}
        </section>
      </div>
    </div>
  );
}

function ExceptionRow({ row, blocking }: { row: ExceptionsView["rows"][number]; blocking: boolean }) {
  const resolve = useResolveFlag(row.site.id);
  const [note, setNote] = useState("");
  const done = row.resolved_at !== null;
  return (
    <li className={done ? "exception-row resolved" : "exception-row"}>
      <span className={blocking ? "site-code-badge crit" : "site-code-badge"}>{flagLabel(row.code)}</span>
      <div className="exception-detail">
        <div>{row.detail}</div>
        <div className="exception-where">
          <Link to={`/sites/${row.site.id}`}>{row.site.name}</Link>
        </div>
      </div>
      {done ? (
        <span className="exception-done">
          Resolved {formatDate(row.resolved_at)}
          {row.resolution_note ? `: ${row.resolution_note}` : ""}
        </span>
      ) : (
        <form
          className="site-resolve"
          onSubmit={(e) => {
            e.preventDefault();
            resolve.mutate({ flagId: row.id, note: note.trim() || null });
          }}
        >
          <input
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="How it was resolved"
            aria-label={`Note for ${row.code} on ${row.site.name}`}
          />
          <button type="submit" className="btn btn-secondary" disabled={resolve.isPending}>
            Resolve
          </button>
        </form>
      )}
      {resolve.isError ? (
        <p className="exception-error" role="alert">
          {resolve.error.message}
        </p>
      ) : null}
    </li>
  );
}
